import { Dialog } from "@headlessui/react";
import FadeLoader from "react-spinners/FadeLoader";

export default function SyncProgressIndicator({
  open,
  uploadProgress: {
    total,
    completed,
    currentLoaded,
    currentTotal,
    currentRecordIndex,
    totalRecords,
    phase,
  },
}) {
  const showDeterminate = total > 0;
  const showPerImage = currentTotal > 0;
  const percent =
    !total || total <= 0
      ? 0
      : Math.max(0, Math.min(100, Math.floor((completed / total) * 100)));

  const perImagePercent = showPerImage
    ? Math.max(
        0,
        Math.min(100, Math.floor((currentLoaded / currentTotal) * 100)),
      )
    : 0;

  const hasRecordInfo =
    typeof currentRecordIndex === "number" &&
    typeof totalRecords === "number" &&
    totalRecords > 0;
  const phaseLabel =
    phase === "uploading" ? "Uploading images" : "Getting images";

  const displayedRecordNumber =
    hasRecordInfo && currentRecordIndex >= 0
      ? currentRecordIndex + 1
      : currentRecordIndex;

  return (
    <Dialog open={open} onClose={() => false} className="relative z-50">
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <div
        data-testid={open ? "syncing" : "notsyncing"}
        className="fixed inset-0 bg-black/30"
        aria-hidden="true"
      />

      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg bg-white shadow p-6">
          <div className="flex items-center justify-center mb-4">
            <FadeLoader loading={true} size={80} data-testid="loader" />
          </div>

          {showDeterminate ? (
            <div>
              {hasRecordInfo && (
                <div className="mb-1 text-center text-xs text-gray-600">
                  {phaseLabel} for record {displayedRecordNumber} of{" "}
                  {totalRecords}...
                </div>
              )}
              <div className="mb-2 text-center text-sm text-gray-700">
                Uploading images {completed} / {total} ({percent}%)
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div
                  className="bg-green-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${percent}%` }}
                  data-testid="images-progress-bar"
                />
              </div>

              {/* Per-image progress */}
              {showPerImage ? (
                <div>
                  <div
                    className="mb-1 text-center text-xs text-gray-600"
                    data-testid="per-image-progress"
                  >
                    Current image: {perImagePercent}% (
                    {Math.round(currentLoaded / 1024)} KB of{" "}
                    {Math.round(currentTotal / 1024)} KB)
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-cyan-600 h-2 rounded-full transition-all duration-150 ease-out"
                      style={{ width: `${perImagePercent}%` }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center text-xs text-gray-500">
                  {"Preparing image..."}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-sm text-gray-700">
              {hasRecordInfo
                ? `${phaseLabel} for record ${displayedRecordNumber} of ${totalRecords}...`
                : "Preparing images..."}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
